// Plan 24b, Task 2.1 — Game HUD screen (Screen 7)
//
// Persistent board-phase overlay: player cards column (left), round pill (top-left),
// pause button (top-right), action bar (bottom-center, only on your turn).
//
// Design system references:
//   - docs/design/tokens.json — layout.hud-padding, layout.hud-player-card-gap,
//     color.ui.panel-bg-alpha, color.text.secondary, color.brand.amber,
//     typography.size.h5, border-radius.pill/lg/md, z-index.hud
//   - docs/design/components.md — §1 Button (icon/sm + primary + secondary),
//     §4 HP Bar, §10 Visual Identity Rules
//   - docs/design/screen-details/board-phase.md §Screen 7

import { createButton } from '../components/button';
import type { UIComponentHandle } from '../components/types';
import { createHPBar } from '../components/hp-bar';
import type { HPBarHandle } from '../components/hp-bar';
import type { ScreenHandle, ScreenMountContext } from './types';
import type { ScreenRouter } from './types';
import type { BoardLiveHudState, BoardMountPayload } from './board-payload';
import type { Player, GameState } from '@shared/game/types';
import type { ServerMessage } from '@shared/protocol';
import { deserializeGameState } from '@shared/protocol';
import type { GsapRegistry } from '../../renderer/vfx/gsap-registry';
import { SHAPE_GLYPH_FOR_COLOR, colorIdFromHex } from './shape-palette';
import { buildInventoryItemMeta } from './item-ui-meta';
import { createBoardTopTimer } from './board-timer';

// ─────────────────────────────────────────────────────────────────────────────
// Tokens (from docs/design/tokens.json)
// ─────────────────────────────────────────────────────────────────────────────

/** color.ui.panel-bg-alpha */
const PANEL_BG_ALPHA = 'rgba(30, 35, 50, 0.82)';
/** color.text.secondary */
const COLOR_TEXT_SECONDARY = '#B0B8C8';
/** color.brand.amber */
const COLOR_AMBER = '#E8A020';
/** typography.size.h5 */
const FONT_SIZE_H5 = 'var(--typography-size-h5, 18px)';
/** typography.size.caption */
const FONT_SIZE_CAPTION = 'var(--typography-size-caption, 14px)';
/** typography.size.label */
const FONT_SIZE_LABEL = 'var(--typography-size-label, 12px)';
/** typography.family.headline */
const FONT_HEADLINE =
  "var(--typography-family-headline, 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif)";
/** typography.family.body */
const FONT_BODY =
  "var(--typography-family-body, 'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif)";
/** border-radius.pill */
const BORDER_RADIUS_PILL = '9999px';
/** border-radius.lg */
const BORDER_RADIUS_LG = '12px';
/** border-radius.md */
const BORDER_RADIUS_MD = '8px';
/** layout.hud-padding */
const HUD_PADDING = '12px';
/** layout.hud-player-card-gap */
const HUD_CARD_GAP = '8px';
/** layout.hud-player-card-width */
const HUD_CARD_WIDTH = '200px';
/** z-index.hud */
const Z_INDEX_HUD = '100';
/** typography.shadow.hud */
const TEXT_SHADOW_HUD = '0 2px 0 #000000, 0 3px 6px rgba(0,0,0,0.50)';
/** color.text.accent */
const COLOR_TEXT_ACCENT = '#E8A020';
/** color.hud.key-gold */
const COLOR_KEY_GOLD = '#FFD700';
/** color.hud.trophy-gold */
const COLOR_TROPHY_GOLD = '#FFD700';
/** color.text.primary */
const COLOR_TEXT_PRIMARY = '#FFFFFF';
/** Max HP constant (Player spec §3.1: hp max 30) */
const MAX_HP = 30;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Convert a numeric hex color (0xRRGGBB) to a CSS hex string. */
function colorToCss(n: number): string {
  return `#${n.toString(16).padStart(6, '0')}`;
}

/** Resolve a shape glyph from the numeric player color (0xRRGGBB).
 *  Uses the canonical palette. Falls back to '●' for unknown values. */
function glyphForColor(colorHex: number): string {
  const colorId = colorIdFromHex(colorHex);
  return colorId ? (SHAPE_GLYPH_FOR_COLOR[colorId] ?? '●') : '●';
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal builders
// ─────────────────────────────────────────────────────────────────────────────

/** Build the round-pill element. */
function _buildRoundPill(state: GameState): HTMLElement {
  const pill = document.createElement('div');
  pill.className = 'hud-round-pill';
  pill.setAttribute('data-region', 'round-pill');
  pill.setAttribute('data-tutorial-target', 'round-pill');
  pill.style.cssText = [
    `background: ${PANEL_BG_ALPHA}`,
    `border-radius: ${BORDER_RADIUS_PILL}`,
    `padding: 4px 16px`,
    `display: inline-block`,
    `font-family: ${FONT_HEADLINE}`,
    `font-size: ${FONT_SIZE_H5}`,
    `color: ${COLOR_TEXT_SECONDARY}`,
    `text-shadow: ${TEXT_SHADOW_HUD}`,
    `letter-spacing: 1px`,
    `user-select: none`,
    `white-space: nowrap`,
  ].join('; ');
  pill.textContent = `Round ${state.round}/${state.maxRounds}`;
  return pill;
}

/** Build a single player card element. */
interface PlayerCardHandle {
  readonly cardEl: HTMLElement;
  readonly hpBarHandle: HPBarHandle;
  readonly keyEl: HTMLElement;
  readonly trophyEl: HTMLElement;
  readonly itemBadge: HTMLElement;
}

function _buildPlayerCard(
  player: Player,
  isActive: boolean,
  gsapRegistry: GsapRegistry,
): PlayerCardHandle {
  const card = document.createElement('div');
  card.className = 'hud-player-card';
  card.setAttribute('data-player-card', '');
  card.setAttribute('data-player-id', player.id);

  const colorCss = colorToCss(player.color);
  const glyph = glyphForColor(player.color);

  // Base card styles
  card.style.cssText = [
    `background: ${PANEL_BG_ALPHA}`,
    `border-radius: ${BORDER_RADIUS_MD}`,
    `padding: 8px`,
    `display: flex`,
    `flex-direction: column`,
    `gap: 4px`,
    `width: ${HUD_CARD_WIDTH}`,
    `box-sizing: border-box`,
    `position: relative`,
    ...(isActive
      ? [`border: 3px solid ${COLOR_AMBER}`, `box-shadow: 0 0 12px rgba(232,160,32,0.50)`]
      : [`border: 2px solid rgba(255, 255, 255, 0.10)`]),
    ...(player.isDead ? [`opacity: 0.35`] : []),
  ].join('; ');

  if (isActive) {
    card.setAttribute('data-active', 'true');
  }

  // ── Header row: color swatch + glyph + name ──────────────────────────────

  const headerRow = document.createElement('div');
  headerRow.style.cssText = [`display: flex`, `align-items: center`, `gap: 6px`].join('; ');

  const swatch = document.createElement('div');
  swatch.className = 'hud-player-card__swatch';
  swatch.style.cssText = [
    `width: 16px`,
    `height: 16px`,
    `border-radius: 3px`,
    `background-color: ${colorCss}`,
    `flex-shrink: 0`,
  ].join('; ');

  const glyphEl = document.createElement('span');
  glyphEl.className = 'hud-player-card__glyph';
  glyphEl.textContent = glyph;
  glyphEl.style.cssText = [`font-size: 12px`, `color: ${colorCss}`, `flex-shrink: 0`].join('; ');

  const nameEl = document.createElement('span');
  nameEl.className = 'hud-player-card__name';
  nameEl.textContent = player.name;
  nameEl.style.cssText = [
    `font-family: ${FONT_HEADLINE}`,
    `font-size: ${FONT_SIZE_CAPTION}`,
    `color: ${COLOR_TEXT_PRIMARY}`,
    `text-shadow: ${TEXT_SHADOW_HUD}`,
    `overflow: hidden`,
    `text-overflow: ellipsis`,
    `white-space: nowrap`,
    `flex: 1`,
    `min-width: 0`,
  ].join('; ');

  headerRow.appendChild(swatch);
  headerRow.appendChild(glyphEl);
  headerRow.appendChild(nameEl);
  card.appendChild(headerRow);

  // ── HP bar ────────────────────────────────────────────────────────────────

  const hpBarMount = document.createElement('div');
  hpBarMount.style.cssText = `width: 100%; box-sizing: border-box;`;
  card.appendChild(hpBarMount);

  const hpBarHandle = createHPBar({
    gsapRegistry: gsapRegistry,
    mount: hpBarMount,
    currentHP: player.hp,
    maxHP: MAX_HP,
    height: 'detail',
    showValue: true,
    showOvershield: true,
  });

  // ── Stats row: keys + trophies + items ────────────────────────────────────

  const statsRow = document.createElement('div');
  statsRow.style.cssText = [
    `display: flex`,
    `align-items: center`,
    `gap: 8px`,
    `font-family: ${FONT_BODY}`,
    `font-size: ${FONT_SIZE_LABEL}`,
    `color: ${COLOR_TEXT_SECONDARY}`,
  ].join('; ');

  // Key count
  const keyEl = document.createElement('span');
  keyEl.className = 'hud-player-card__keys';
  keyEl.style.color = COLOR_KEY_GOLD;
  keyEl.textContent = `🔑 ${player.keys}`;

  // Trophy count
  const trophyEl = document.createElement('span');
  trophyEl.className = 'hud-player-card__trophies';
  trophyEl.style.color = COLOR_TROPHY_GOLD;
  trophyEl.textContent = `🏆 ${player.trophies}`;

  // Item badge
  const itemBadge = document.createElement('span');
  itemBadge.className = 'hud-player-card__items';
  itemBadge.style.cssText = [
    `background: rgba(232,160,32,0.20)`,
    `border-radius: ${BORDER_RADIUS_PILL}`,
    `padding: 1px 6px`,
    `color: ${COLOR_TEXT_ACCENT}`,
    `font-size: ${FONT_SIZE_LABEL}`,
  ].join('; ');
  itemBadge.textContent = `📦 ${player.items.length}`;

  statsRow.appendChild(keyEl);
  statsRow.appendChild(trophyEl);
  statsRow.appendChild(itemBadge);
  card.appendChild(statsRow);

  return { cardEl: card, hpBarHandle, keyEl, trophyEl, itemBadge };
}

/** Build the vertical player cards column. */
function _buildPlayerCardColumn(
  state: GameState,
  _localPlayerId: string,
  gsapRegistry: GsapRegistry,
): { columnEl: HTMLElement; cardHandles: Map<string, PlayerCardHandle> } {
  const column = document.createElement('div');
  column.className = 'hud-player-cards';
  column.setAttribute('data-region', 'player-cards');
  column.setAttribute('data-tutorial-target', 'player-cards');
  column.style.cssText = [`display: flex`, `flex-direction: column`, `gap: ${HUD_CARD_GAP}`].join(
    '; ',
  );

  const cardHandles = new Map<string, PlayerCardHandle>();

  // Sort: alive first, dead last
  const activePid = state.turnOrder[state.currentPlayerIndex];
  const alive = state.turnOrder.filter((pid) => !state.players.get(pid)?.isDead);
  const dead = state.turnOrder.filter((pid) => state.players.get(pid)?.isDead);
  const ordered = [...alive, ...dead];

  for (const pid of ordered) {
    const player = state.players.get(pid);
    if (!player) continue;
    const isActive = pid === activePid;
    const handle = _buildPlayerCard(player, isActive, gsapRegistry);
    column.appendChild(handle.cardEl);
    cardHandles.set(pid, handle);
  }

  return { columnEl: column, cardHandles };
}

/** Determine if the action bar should be visible.
 *  Visible when: phase is item_phase OR rolling, AND current player is localPlayerId. */
function _isActionBarVisible(state: GameState, localPlayerId: string): boolean {
  const isActionPhase = state.phase === 'item_phase' || state.phase === 'rolling';
  const currentPid = state.turnOrder[state.currentPlayerIndex];
  return isActionPhase && currentPid === localPlayerId;
}

function _isFrozenForCurrentRound(state: GameState, playerId: string): boolean {
  const player = state.players.get(playerId);
  return (
    player?.statusEffects.frozenUntil != null && player.statusEffects.frozenUntil >= state.round
  );
}

/** Build the bottom action bar. Returns the bar element and button handles (caller must dispose handles). */
function _buildActionBar(
  state: GameState,
  localPlayerId: string,
  payload: BoardMountPayload,
  gsapRegistry: GsapRegistry,
  router: ScreenRouter,
  actionControlsLocked: boolean,
): { barEl: HTMLElement; buttonHandles: UIComponentHandle[]; timerId: number | null } {
  const visible = _isActionBarVisible(state, localPlayerId);
  const timerId: number | null = null;

  const bar = document.createElement('div');
  bar.className = 'hud-action-bar';
  bar.setAttribute('data-region', 'action-bar');
  bar.setAttribute('data-tutorial-target', 'action-bar');
  bar.style.cssText = [
    `background: ${PANEL_BG_ALPHA}`,
    `border-radius: ${BORDER_RADIUS_LG}`,
    `padding: 16px`,
    `display: flex`,
    `align-items: center`,
    `gap: 16px`,
    `position: absolute`,
    `bottom: ${HUD_PADDING}`,
    `left: 50%`,
    `transform: translateX(-50%)`,
    `z-index: ${Z_INDEX_HUD}`,
    ...(visible ? [] : [`display: none`]),
  ].join('; ');

  // Set display:none explicitly if not visible (last-wins in cssText)
  if (!visible) {
    bar.style.display = 'none';
  }

  // Find local player for item count
  const localPlayer = state.players.get(localPlayerId);
  const itemCount = localPlayer?.items.length ?? 0;
  const hasItems = itemCount > 0;

  const isRollingPhase = state.phase === 'rolling';
  const currentPid = state.turnOrder[state.currentPlayerIndex];
  const currentPlayerFrozen = currentPid != null && _isFrozenForCurrentRound(state, currentPid);

  const rollDiceBtn = createButton({
    gsapRegistry,
    variant: 'primary',
    size: 'small',
    label: currentPlayerFrozen ? 'End Move' : 'Roll Dice',
    disabled: actionControlsLocked,
    onClick: () => {
      if (actionControlsLocked) return;
      if (currentPid == null) return;
      if (currentPlayerFrozen) {
        payload.send(state.phase === 'item_phase' ? { type: 'skip_item' } : { type: 'roll_dice' });
        return;
      }
      router.navigate('dice-roll', {
        ...payload,
        state,
        rollerPlayerId: currentPid,
        autoRoll: true,
      });
    },
  });
  rollDiceBtn.element.setAttribute('data-action', 'roll-dice');
  rollDiceBtn.element.setAttribute('data-tutorial-target', 'roll-dice');
  rollDiceBtn.element.toggleAttribute('data-action-locked', actionControlsLocked);
  bar.appendChild(rollDiceBtn.element);

  const itemTrayBtn = createButton({
    gsapRegistry,
    variant: 'secondary',
    size: 'small',
    label: `Items (${itemCount})`,
    disabled: actionControlsLocked || !hasItems || isRollingPhase,
    onClick: () => {
      if (actionControlsLocked) return;
      if (!hasItems || isRollingPhase) return;
      router.navigate('inventory', {
        ...payload,
        state,
        itemMeta: buildInventoryItemMeta(),
        inventoryMode: 'use',
      });
    },
  });
  itemTrayBtn.element.setAttribute('data-action', 'item-tray');
  itemTrayBtn.element.setAttribute('data-tutorial-target', 'items');
  itemTrayBtn.element.toggleAttribute('data-action-locked', actionControlsLocked);
  bar.appendChild(itemTrayBtn.element);

  return { barEl: bar, buttonHandles: [rollDiceBtn, itemTrayBtn], timerId };
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createGameHUDScreen(ctx: ScreenMountContext<BoardMountPayload>): ScreenHandle {
  const { gsapRegistry, host, payload, router } = ctx;
  const { localPlayerId } = payload;

  let disposed = false;
  let latestState = payload.state;
  let actionControlsLocked = payload.isActionControlLocked?.() ?? false;

  // ── Build root ────────────────────────────────────────────────────────────

  const root = document.createElement('div');
  root.className = 'screen-board-hud';
  root.setAttribute('data-screen-id', 'board-hud');
  root.style.cssText = [
    `position: absolute`,
    `inset: 0`,
    `pointer-events: none`,
    `z-index: ${Z_INDEX_HUD}`,
    `overflow: hidden`,
  ].join('; ');

  // Allow interactive children (buttons, etc.) to receive events
  // by keeping pointer-events:auto on interactive descendants.

  // ── Left column wrapper ───────────────────────────────────────────────────
  // Contains round pill + player cards column
  const leftCol = document.createElement('div');
  leftCol.style.cssText = [
    `position: absolute`,
    `top: ${HUD_PADDING}`,
    `left: ${HUD_PADDING}`,
    `display: flex`,
    `flex-direction: column`,
    `gap: ${HUD_CARD_GAP}`,
    `pointer-events: none`,
  ].join('; ');

  // ── Initial state render ──────────────────────────────────────────────────

  const roundPillEl = _buildRoundPill(payload.state);
  const activeTimer = createBoardTopTimer(payload, { subscribe: false });
  const initialColumn = _buildPlayerCardColumn(payload.state, localPlayerId, gsapRegistry);
  let cardColumnEl = initialColumn.columnEl;
  let playerCardHandles = initialColumn.cardHandles;
  const initialBar = _buildActionBar(
    payload.state,
    localPlayerId,
    payload,
    gsapRegistry,
    router,
    actionControlsLocked,
  );
  let actionBarEl = initialBar.barEl;
  let actionBarBtnHandles: UIComponentHandle[] = initialBar.buttonHandles;
  let actionBarTimerId: number | null = initialBar.timerId;

  leftCol.appendChild(roundPillEl);
  leftCol.appendChild(cardColumnEl);
  root.appendChild(leftCol);
  root.appendChild(activeTimer.element);

  // ── Action bar (bottom-center) ────────────────────────────────────────────

  root.appendChild(actionBarEl);

  const inventoryFab = document.createElement('button');
  inventoryFab.type = 'button';
  inventoryFab.setAttribute('data-action', 'open-inventory');
  inventoryFab.setAttribute('data-tutorial-target', 'items-fab');
  inventoryFab.style.cssText = [
    `position: absolute`,
    `right: ${HUD_PADDING}`,
    `bottom: ${HUD_PADDING}`,
    'min-width: 132px',
    'height: 46px',
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'gap: 8px',
    `background: ${PANEL_BG_ALPHA}`,
    'border: 1px solid rgba(255,255,255,0.18)',
    `border-radius: ${BORDER_RADIUS_MD}`,
    'box-shadow: 0 6px 18px rgba(0,0,0,0.30)',
    `font-family: ${FONT_HEADLINE}`,
    `font-size: ${FONT_SIZE_CAPTION}`,
    'font-weight: 800',
    `color: ${COLOR_TEXT_PRIMARY}`,
    'text-transform: uppercase',
    'text-shadow: 0 1px 0 #000000',
    'pointer-events: auto',
    'cursor: pointer',
  ].join('; ');
  inventoryFab.addEventListener('click', openInventoryFromFab);
  root.appendChild(inventoryFab);
  refreshInventoryFab(payload.state);

  // ── Mount to host ─────────────────────────────────────────────────────────

  host.appendChild(root);

  // ── State subscription ────────────────────────────────────────────────────

  const unsubscribe = payload.subscribe((msg: ServerMessage) => {
    if (msg.type !== 'state_sync') return;
    const newState = deserializeGameState(msg.state);
    _rebuild(newState);
  });
  const unsubscribeLiveHud = payload.subscribeLiveHudState?.((state) => {
    applyLiveHudState(state);
  });
  const unsubscribeActionLock = payload.subscribeActionControlLock?.((locked) => {
    if (disposed || actionControlsLocked === locked) return;
    actionControlsLocked = locked;
    rebuildActionBar(latestState);
    refreshInventoryFab(latestState);
  });

  // ── _rebuild — mutate DOM in-place on state updates ───────────────────────

  function _rebuild(state: GameState): void {
    if (disposed) return;
    latestState = state;

    // Update round pill text
    const newPillText = `Round ${state.round}/${state.maxRounds}`;
    roundPillEl.textContent = newPillText;
    activeTimer.updateFromGameState(state);

    // Rebuild player cards column (replace children)
    const { columnEl: newColumn, cardHandles: newCardHandles } = _buildPlayerCardColumn(
      state,
      localPlayerId,
      gsapRegistry,
    );

    // Dispose old HP bars
    for (const handle of playerCardHandles.values()) {
      handle.hpBarHandle.dispose();
    }

    // Replace card column in DOM
    leftCol.replaceChild(newColumn, cardColumnEl);
    cardColumnEl = newColumn;
    playerCardHandles = newCardHandles;

    rebuildActionBar(state);
    refreshInventoryFab(state);
  }

  function rebuildActionBar(state: GameState): void {
    if (disposed) return;
    // Rebuild action bar: dispose old button handles before replacing
    for (const h of actionBarBtnHandles) {
      h.dispose();
    }
    if (actionBarTimerId != null) {
      window.clearInterval(actionBarTimerId);
      actionBarTimerId = null;
    }
    const newActionBar = _buildActionBar(
      state,
      localPlayerId,
      payload,
      gsapRegistry,
      router,
      actionControlsLocked,
    );
    root.replaceChild(newActionBar.barEl, actionBarEl);
    actionBarEl = newActionBar.barEl;
    actionBarBtnHandles = newActionBar.buttonHandles;
    actionBarTimerId = newActionBar.timerId;
  }

  function openInventoryFromFab(): void {
    if (disposed) return;
    const localPlayer = latestState.players.get(localPlayerId);
    if (localPlayer == null) return;
    if (localPlayer.items.length === 0) return;
    const canUse =
      latestState.phase === 'item_phase' &&
      latestState.turnOrder[latestState.currentPlayerIndex] === localPlayerId &&
      !actionControlsLocked;
    if (!canUse) return;
    router.navigate('inventory', {
      ...payload,
      state: latestState,
      itemMeta: buildInventoryItemMeta(),
      inventoryMode: 'use',
    });
  }

  function refreshInventoryFab(state: GameState): void {
    const localPlayer = state.players.get(localPlayerId);
    const itemCount = localPlayer?.items.length ?? 0;
    const canUse =
      state.phase === 'item_phase' &&
      state.turnOrder[state.currentPlayerIndex] === localPlayerId &&
      !actionControlsLocked &&
      itemCount > 0;
    inventoryFab.textContent = `Items (${itemCount})`;
    inventoryFab.style.display = canUse ? 'flex' : 'none';
  }

  function applyLiveHudState(state: BoardLiveHudState): void {
    if (disposed) return;
    roundPillEl.textContent = `Round ${state.round}/${state.maxRounds}`;
    activeTimer.updateFromLiveHudState(state);

    for (const player of state.players) {
      const handle = playerCardHandles.get(player.id);
      if (handle == null) continue;
      handle.hpBarHandle.setHP(player.hp, MAX_HP);
      handle.keyEl.textContent = `🔑 ${player.keys}`;
      handle.trophyEl.textContent = `🏆 ${player.trophies}`;
      handle.itemBadge.textContent = `📦 ${player.itemCount}`;
      handle.cardEl.style.opacity = player.isDead ? '0.35' : '';
    }
  }

  // ── dispose ───────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    unsubscribe();
    unsubscribeLiveHud?.();
    unsubscribeActionLock?.();

    // Dispose all HP bars
    for (const handle of playerCardHandles.values()) {
      handle.hpBarHandle.dispose();
    }
    playerCardHandles.clear();

    // Dispose action bar button handles
    for (const h of actionBarBtnHandles) {
      h.dispose();
    }
    actionBarBtnHandles = [];
    if (actionBarTimerId != null) {
      window.clearInterval(actionBarTimerId);
      actionBarTimerId = null;
    }
    activeTimer.dispose();

    inventoryFab.removeEventListener('click', openInventoryFromFab);

    // Remove root from parent
    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
  }

  return { element: root, dispose };
}
